import { useNavigation } from '@react-navigation/native'
import React, { FunctionComponent } from 'react'
import styled from 'styled-components/native'

import { ApiError } from 'api/ApiError'
import { MaintenancePageType } from 'api/gen'
import { useAuthContext } from 'features/auth/context/AuthContext'
import { SecondButtonList } from 'features/identityCheck/components/SecondButtonList'
import { logIdentityCheckUnavailable } from 'features/identityCheck/pages/helpers/logIdentityCheckUnavailable'
import { useGetStepperInfoQuery } from 'features/identityCheck/queries/useGetStepperInfoQuery'
import { useUbbleIdentificationMutation } from 'features/identityCheck/queries/useUbbleIdentificationMutation'
import { UseNavigationType } from 'features/navigation/navigators/RootNavigator/types'
import { getSubscriptionHookConfig } from 'features/navigation/navigators/SubscriptionStackNavigator/getSubscriptionHookConfig'
import { getSubscriptionPropConfig } from 'features/navigation/navigators/SubscriptionStackNavigator/getSubscriptionPropConfig'
import { getComputedAccessibilityLabel } from 'shared/accessibility/helpers/getComputedAccessibilityLabel'
import { AccessibleUnorderedList } from 'ui/components/accessibility/AccessibleUnorderedList'
import { HeroButtonListWithOnPress } from 'ui/components/buttons/HeroButtonListWithOnPress'
import { SeparatorWithText } from 'ui/components/SeparatorWithText'
import { ViewGap } from 'ui/components/ViewGap/ViewGap'
import { PageWithHeader } from 'ui/pages/PageWithHeader'
import { IdCard as InitialIdCard } from 'ui/svg/icons/IdCard'
import { LostId as InitialLostId } from 'ui/svg/icons/LostId'
import { NoId as InitialNoId } from 'ui/svg/icons/NoId'
import { Typo } from 'ui/theme'
import { getHeadingAttrs } from 'ui/theme/typographyAttrs/getHeadingAttrs'

export const SelectIDStatus: FunctionComponent = () => {
  const { data: subscription } = useGetStepperInfoQuery()
  const { user } = useAuthContext()
  const { navigate, replace } = useNavigation<UseNavigationType>()

  const { mutateAsync: mutateUbbleIdentification } = useUbbleIdentificationMutation()

  const onPress = async () => {
    try {
      const identificationSessionResponse = await mutateUbbleIdentification()

      // We don't use the helper because it would require making the navigation param identificationUrl optional (in SubscriptionStackTypes.ts) to satisfy typing
      navigate('SubscriptionStackNavigator', {
        screen: 'UbbleWebview',
        params: {
          identificationUrl: identificationSessionResponse.identificationUrl,
        },
      })
    } catch (err) {
      const error = (err as ApiError)?.content?.code
      if (error === 'IDCHECK_ALREADY_PROCESSED') {
        replace(...getSubscriptionHookConfig('IdentityCheckPending'))
      } else {
        const withDMS = subscription?.maintenancePageType === MaintenancePageType['with-dms']
        logIdentityCheckUnavailable({
          source: 'SelectIDStatus',
          error: err,
          user,
          subscription,
          params: { withDMS },
        })
        replace(...getSubscriptionHookConfig('IdentityCheckUnavailable', { withDMS }))
      }
    }
  }

  return (
    <PageWithHeader
      title="Identification"
      scrollChildren={
        <Container>
          <TitleContainer>
            <StyledTitle4>As-tu ta pièce d’identité avec toi&nbsp;?</StyledTitle4>
          </TitleContainer>
          <StyledText>
            <Typo.Body>Tu dois avoir ta pièce d’identité </Typo.Body>
            <Typo.BodyAccent>originale </Typo.BodyAccent>
            <Typo.Body>et </Typo.Body>
            <Typo.BodyAccent>en cours de validité </Typo.BodyAccent>
            <Typo.Body>avec toi.</Typo.Body>
          </StyledText>
          <StyledViewGap gap={7}>
            <HeroButtonListWithOnPress
              accessibilityLabel={accessibilityLabel}
              Title={<Typo.BodyAccent>{title}</Typo.BodyAccent>}
              Subtitle={<Typo.BodyAccentXs>{subtitle}</Typo.BodyAccentXs>}
              Icon={<IdCard />}
              onPress={onPress}
            />
            <SeparatorWithText label="ou" />
            <AccessibleUnorderedList
              items={[FirstOtherOption, SecondOtherOption]}
              Separator={buttonListSeparator}
              withPadding
            />
          </StyledViewGap>
        </Container>
      }
    />
  )
}
const TitleContainer = styled.View(({ theme }) => ({
  marginVertical: theme.designSystem.size.spacing.l,
}))

const StyledViewGap = styled(ViewGap)(({ theme }) => ({
  marginTop: theme.designSystem.size.spacing.xxxxl,
}))

const IdCard = styled(InitialIdCard).attrs(({ theme }) => ({
  color: theme.designSystem.color.icon.brandPrimary,
}))``

const title = 'J’ai ma pièce d’identité en cours de validité'
const subtitle = 'Les copies ne sont pas acceptées'
const accessibilityLabel = getComputedAccessibilityLabel(title, subtitle)

const NoId = styled(InitialNoId).attrs(({ theme }) => ({
  color: theme.designSystem.color.icon.brandPrimary,
}))``

const FirstOtherOption = (
  <SecondButtonList
    label="Je n’ai pas ma pièce d’identité originale avec moi"
    leftIcon={NoId}
    navigateTo={getSubscriptionPropConfig('ComeBackLater')}
  />
)

const LostId = styled(InitialLostId).attrs(({ theme }) => ({
  color: theme.designSystem.color.icon.brandPrimary,
}))``

const SecondOtherOption = (
  <SecondButtonList
    label="Ma pièce d’identité est expirée ou perdue"
    leftIcon={LostId}
    navigateTo={getSubscriptionPropConfig('ExpiredOrLostID')}
  />
)

const Separator = styled.View(({ theme }) => ({
  height: theme.designSystem.size.spacing.xl,
}))
const buttonListSeparator = <Separator />

const Container = styled.View(({ theme }) => ({
  marginHorizontal: theme.designSystem.size.spacing.xs,
  marginVertical: theme.designSystem.size.spacing.xxl,
}))

const StyledTitle4 = styled(Typo.Title4).attrs(getHeadingAttrs(2))({
  textAlign: 'center',
})

const StyledText = styled(Typo.Body)({
  textAlign: 'center',
})
